import React from "react";
import { useOutletContext } from "react-router-dom";

import { UnifiedDataTable } from "../../../layout/unified-data-table/ui/unified-data-table";
import type {
  UnifiedDataTableColumnRenderers,
  UnifiedDataTableEmptyStateIconRenderers,
  UnifiedTableRow,
} from "../../../shared/ui/data-table/model/data-table-contract";
import { DicomStudiesIcon } from "../../../shared/ui/icons/dicom-studies-icon";
import { dicomStudiesRecord } from "../model/dicom-studies";

import * as Styled from "./dicom-studies.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

const getImageValue = (row: UnifiedTableRow) =>
  typeof row.previewImage === "string" ? row.previewImage : "";

const getAltValue = (row: UnifiedTableRow) =>
  typeof row.previewAlt === "string" ? row.previewAlt : "DICOM study preview";

const getUrlValue = (value: unknown) =>
  typeof value === "string" ? value : "";

const escapeHtmlAttribute = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const openDicomImage = (imageUrl: string, alt: string) => {
  if (!imageUrl) {
    return;
  }

  const imageWindow = window.open("", "_blank");

  if (!imageWindow) {
    window.location.href = imageUrl;
    return;
  }

  const escapedUrl = escapeHtmlAttribute(imageUrl);
  const escapedAlt = escapeHtmlAttribute(alt);

  imageWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>${escapedAlt}</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html,
      body {
        width: 100%;
        height: 100%;
        margin: 0;
        background: #000;
      }

      body {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      img {
        display: block;
        width: auto;
        height: 100vh;
        max-width: 100vw;
        object-fit: contain;
      }
    </style>
  </head>
  <body>
    <img src="${escapedUrl}" alt="${escapedAlt}" />
  </body>
</html>`);
  imageWindow.document.close();
  imageWindow.opener = null;
};

export const DicomStudies = () => {
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const emptyStateIconRenderers =
    React.useMemo<UnifiedDataTableEmptyStateIconRenderers>(
      () => ({
        dicomStudies: <DicomStudiesIcon size={40} color="#777b7b" />,
      }),
      [],
    );

  const columnRenderers = React.useMemo<UnifiedDataTableColumnRenderers>(
    () => ({
      dicomPreview: ({ row }) => (
        <Styled.PreviewImage src={getImageValue(row)} alt={getAltValue(row)} />
      ),
      dicomAction: ({ row, value }) => (
        <Styled.ViewButton
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openDicomImage(getUrlValue(value), getAltValue(row));
          }}
        >
          <i className="pi pi-eye" aria-hidden="true" />
          View Dicom
        </Styled.ViewButton>
      ),
    }),
    [],
  );

  return (
    <Styled.Container>
      <Styled.Title>DICOM Studies</Styled.Title>
      <Styled.TableScope>
        <UnifiedDataTable
          record={dicomStudiesRecord}
          columnRenderers={columnRenderers}
          emptyStateIconRenderers={emptyStateIconRenderers}
          scrollContainerRef={contentScrollerRef}
        />
      </Styled.TableScope>
    </Styled.Container>
  );
};
